/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import CheckInFormBase from "@/src/components/Checkin/Common/CheckInFormBase";
import checkInFormBaseStyle from "@/src/components/Checkin/Common/checkInFormBaseStyle";
import { requiredValidator } from "@/src/services/validationRules";
import * as loginActions from "@/src/store/login/actions";
import { getLpByLocation } from "@/src/services/landingPagesList";
import { useLocation, useNavigate } from "react-router";
import MenuItem from "@mui/material/MenuItem";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { getProductData } from "@/src/services/productService";
import { createResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getPartnerNameByIri, PARTNER_IRIS } from "@/src/constants/partnerIris";
import type { PartnerIri } from "@/src/constants/partnerIris";

type EmploymentLawCheckInProps = {
  idPrefix?: string;
  color?: "default" | "grey";
  target?: string;
  checkinHeading?: string;
  partner?: PartnerIri;
};

const EmploymentLawCheckIn = ({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Fall schildern und professionelle Hilfe erhalten",
  partner = PARTNER_IRIS.ADVOCARD,
}: EmploymentLawCheckInProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    product: "",
    email: "",
  };

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      prelead: {
        initialProduct: values.product,
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: getProductData(values.product, "apiUri"),
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[checkInFormBaseStyle.formPaper, checkInFormBaseStyle[color]]}>
      <Box id={idPrefix + "-checkin"} />
      <Typography variant={"h5"} sx={checkInFormBaseStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <CheckInFormBase
          formFields={
            <ValidatorSelectField label="Ihr Anliegen" name={`product`} validators={[requiredValidator("Bitte geben Sie die Art Ihres Anliegens an.")]}>
              <MenuItem value={"settlement"}>Kündigungsschutzklage</MenuItem>
              <MenuItem value={"employmentContract"}>Anderes arbeitsrechtliches Anliegen</MenuItem>
            </ValidatorSelectField>
          }
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          isLoading={isLoading}
        />
      </FormProvider>
    </Paper>
  );
};

export default EmploymentLawCheckIn;
